import { ReactComponent as TackIcon } from '../../../../../assets/tack.svg';
import { FieldType, WorksheetSchemaField } from '../../../../../types/custom-worksheet-v2';
import { PencilSquareIcon, QuestionMarkCircleIcon, TrashIcon } from '@heroicons/react/20/solid';
import { TooltipV2 } from '@cimplico/components';
import { removeAdditionalField, setCurrentEditingField } from '../../store/slice';
import { useDispatch, useSelector } from 'react-redux';
import { useState } from 'react';
import FlashBackground from '../FlashBackground';
import CWPropertyName from '../../../../../components/custom-worksheet/CWPropertyName';
import CWField from './CWField';
import NewOrEditAdditionalField from './NewOrEditAdditionalField';
import DeleteAdditionalFieldConfirmation from './DeleteAdditionalFieldConfirmation';
import QuickActions from './QuickActions';
import { selectShowHidden } from '../../store/selectors';

type StandaloneFieldProps = {
    allowRedirect?: boolean;
    isEditing?: boolean;
    allowedTypes?: FieldType[];
    field: WorksheetSchemaField;
    headingSize?: 'xl' | '2xl' | 'base' | 'sm' | 'lg';
    onEditing?: () => void;
};

function StandaloneField({
    allowRedirect,
    isEditing,
    allowedTypes,
    field,
    headingSize = 'base',
    onEditing,
}: StandaloneFieldProps): JSX.Element {
    const dispatch = useDispatch();
    const showHidden = useSelector(selectShowHidden);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

    function deleteAdditionalField(): void {
        // If field currently has a value, show a confirmation before deleting
        if (field.value !== null) {
            setShowDeleteConfirmation(true);
        } else {
            dispatch(removeAdditionalField(field.id));
        }
    }

    if (isEditing && field.isAdditionalField) {
        return (
            <NewOrEditAdditionalField
                allowedTypes={allowedTypes || []}
                field={field}
                onClose={(resetEditingField?: boolean): void => {
                    onEditing && onEditing();
                    if (resetEditingField !== false) {
                        dispatch(setCurrentEditingField(undefined));
                    }
                }}
            />
        );
    }

    return field.hidden && !showHidden ? (
        <></>
    ) : (
        <div id={field.id} className={`scroll-mt-8 flex justify-between items-center relative group`}>
            {showDeleteConfirmation && (
                <DeleteAdditionalFieldConfirmation id={field.id} name={field.name} onClose={(): void => setShowDeleteConfirmation(false)} />
            )}
            <FlashBackground id={field.id} />
            {field.isAdditionalField && (
                <div className="absolute -left-10 top-0 cursor-pointer gap-2 items-center flex w-full h-full">
                    <PencilSquareIcon
                        className="w-3 h-3 text-gray-500 hover:text-indigo-500 hidden group-hover:block"
                        onClick={(): void => {
                            onEditing && onEditing();
                            dispatch(setCurrentEditingField(field.id));
                        }}
                    />
                    <TrashIcon
                        className="w-3 h-3 text-gray-500 hover:text-indigo-500 hidden group-hover:block"
                        onClick={deleteAdditionalField}
                    />
                </div>
            )}
            {field.pinned && (
                <div className="absolute -left-5 top-3">
                    <TackIcon style={{ fill: field.pinned ? '#16A249' : undefined }} />
                </div>
            )}
            <div className="flex gap-2 items-center">
                <CWPropertyName property={field} headingSize={headingSize} allowRedirect={allowRedirect} />
                {field.helperText && (
                    <TooltipV2 message={field.helperText}>
                        <QuestionMarkCircleIcon className="w-4 h-4 text-gray-500" />
                    </TooltipV2>
                )}
            </div>
            <div className="ml-auto">
                <QuickActions actions={field.quickActions} field={field} />
            </div>
            <div className="w-[300px] mr-0.5">
                <CWField field={field} allowRedirect={allowRedirect} />
            </div>
        </div>
    );
}

export default StandaloneField;
